import { useEffect, useRef, useState } from "react";
import { api, payload } from "./lib/api";
import { uploadMedia } from "./lib/upload-media";
import type { MediaAsset, MenuCategory, MenuItem, RestaurantMenu } from "./types";

const emptyMenu = (): RestaurantMenu => ({
  title: "Menu",
  description: "",
  currency: "EUR",
  published: true,
  categories: [],
});
const newCategory = (): MenuCategory => ({
  id: crypto.randomUUID(),
  name: "Nuova categoria",
  description: "",
  displayStyle: "grid",
  visible: true,
  items: [],
});
const newItem = (): MenuItem => ({
  id: crypto.randomUUID(),
  name: "Nuovo elemento",
  description: "",
  priceCents: null,
  imageUrl: null,
  origin: "",
  dietaryTags: [],
  available: true,
  featured: false,
  variants: [],
});
const money = (value: number | null, currency: RestaurantMenu["currency"]) =>
  value === null
    ? "Prezzo non indicato"
    : new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(value / 100);
const toCents = (value: string) => {
  const normalized = value.trim().replace(",", ".");
  if (!normalized) return null;
  const number = Number(normalized);
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) : null;
};

function resolvedImage(value: string | null, siteBaseUrl: string) {
  if (!value) return "";
  try { return new URL(value, `${siteBaseUrl.replace(/\/$/, "")}/`).toString(); } catch { return value; }
}

export default function RestaurantMenuManager({ siteId, canEdit, siteBaseUrl }: { siteId: string; canEdit: boolean; siteBaseUrl: string }) {
  const [menu, setMenu] = useState<RestaurantMenu | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const initialRef = useRef("");

  useEffect(() => {
    api<{ menu: RestaurantMenu | null }>(`/api/sites/${siteId}/restaurant-menu`)
      .then(({ menu: current }) => {
        const next = current || emptyMenu();
        setMenu(next);
        initialRef.current = JSON.stringify(next);
        setSelectedCategoryId(next.categories[0]?.id || "");
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : "Menu non disponibile"));
  }, [siteId]);

  useEffect(() => {
    if (!menu) return;
    setDirty(Boolean(initialRef.current) && JSON.stringify(menu) !== initialRef.current);
  }, [menu]);

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const selectedCategory = menu?.categories.find((category) => category.id === selectedCategoryId) || null;
  const selectedItem = selectedCategory?.items.find((item) => item.id === selectedItemId) || null;
  const filtered = (() => {
    const query = search.trim().toLocaleLowerCase("it");
    if (!selectedCategory) return [];
    return query
      ? selectedCategory.items.filter((item) => `${item.name} ${item.description}`.toLocaleLowerCase("it").includes(query))
      : selectedCategory.items;
  })();

  function updateMenu(change: (current: RestaurantMenu) => RestaurantMenu) {
    if (!canEdit) return;
    setNotice("");
    setMenu((current) => current ? change(current) : current);
  }
  function updateCategory(categoryId: string, patch: Partial<MenuCategory>) {
    updateMenu((current) => ({
      ...current,
      categories: current.categories.map((category) => category.id === categoryId ? { ...category, ...patch } : category),
    }));
  }
  function updateItem(itemId: string, patch: Partial<MenuItem>) {
    updateMenu((current) => ({
      ...current,
      categories: current.categories.map((category) => ({
        ...category,
        items: category.items.map((item) => item.id === itemId ? { ...item, ...patch } : item),
      })),
    }));
  }
  function addCategory() {
    const category = newCategory();
    updateMenu((current) => ({ ...current, categories: [...current.categories, category] }));
    setSelectedCategoryId(category.id);
    setSelectedItemId("");
  }
  function addItem() {
    if (!selectedCategory) return;
    const item = newItem();
    updateCategory(selectedCategory.id, { items: [...selectedCategory.items, item] });
    setSelectedItemId(item.id);
  }
  function duplicateItem(item: MenuItem) {
    if (!selectedCategory) return;
    const copy = {
      ...structuredClone(item),
      id: crypto.randomUUID(),
      name: `${item.name} copia`,
      variants: item.variants.map((variant) => ({ ...variant, id: crypto.randomUUID() })),
    };
    updateCategory(selectedCategory.id, { items: [...selectedCategory.items, copy] });
    setSelectedItemId(copy.id);
  }
  function move<T>(values: T[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= values.length) return values;
    const next = [...values];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  }
  async function save() {
    if (!menu || !canEdit) return;
    setBusy("save");
    setError("");
    setNotice("");
    try {
      const result = await api<{ menu: RestaurantMenu }>(
        `/api/sites/${siteId}/restaurant-menu`,
        payload("PUT", menu),
      );
      setMenu(result.menu);
      initialRef.current = JSON.stringify(result.menu);
      setDirty(false);
      setNotice(menu.published ? "Menu salvato e pubblicato sul sito" : "Menu salvato come non pubblicato");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Salvataggio non riuscito");
    } finally {
      setBusy("");
    }
  }
  async function uploadImage(file: File) {
    if (!selectedItem || !canEdit) return;
    setBusy("upload");
    setError("");
    try {
      const result = { media: await uploadMedia<MediaAsset>(siteId, file, selectedItem.name) };
      updateItem(selectedItem.id, { imageUrl: result.media.url });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Caricamento foto non riuscito");
    } finally {
      setBusy("");
    }
  }

  if (!menu && !error) return <div className="menu-loading"><i /><p>Preparazione del menu ristorante</p></div>;
  if (!menu) return <div className="notice error">{error}</div>;

  return (
    <div className="restaurant-manager">
      <header className="menu-manager-head">
        <div>
          <p className="eyebrow indigo">Menu ristorante</p>
          <h2>{menu.title}</h2>
          <p>Categorie, piatti, drink, cocktail, varianti e disponibilità in un unico pannello.</p>
        </div>
        <div className="menu-head-actions">
          <label className="publish-switch">
            <input type="checkbox" checked={menu.published} disabled={!canEdit} onChange={(event) => updateMenu((current) => ({ ...current, published: event.target.checked }))} />
            <span />
            {menu.published ? "Pubblicato" : "Non pubblicato"}
          </label>
          <button className="primary" disabled={!canEdit || busy === "save" || !dirty} onClick={() => void save()}>
            {busy === "save" ? "Salvataggio…" : dirty ? "Salva e aggiorna il sito" : "Tutto salvato"}
          </button>
        </div>
      </header>
      {error && <div className="notice error">{error}</div>}
      {notice && <div className="notice success">{notice}</div>}
      <div className="menu-workbench">
        <aside className="menu-categories">
          <div className="menu-panel-title"><strong>Categorie</strong><span>{menu.categories.length}</span></div>
          <div className="category-list">
            {menu.categories.map((category, index) => (
              <button key={category.id} className={selectedCategoryId === category.id ? "active" : ""} onClick={() => { setSelectedCategoryId(category.id); setSelectedItemId(""); }}>
                <span className="drag-dots">⠿</span>
                <span><strong>{category.name}</strong><small>{category.items.length} elementi</small></span>
                {!category.visible && <i>nascosta</i>}
                {canEdit && <span className="order-controls">
                  <b role="button" tabIndex={0} aria-label="Sposta categoria su" onClick={(event) => { event.stopPropagation(); updateMenu((current) => ({ ...current, categories: move(current.categories, index, -1) })); }}>↑</b>
                  <b role="button" tabIndex={0} aria-label="Sposta categoria giù" onClick={(event) => { event.stopPropagation(); updateMenu((current) => ({ ...current, categories: move(current.categories, index, 1) })); }}>↓</b>
                </span>}
              </button>
            ))}
          </div>
          {canEdit && <button className="menu-add-category" onClick={addCategory}>＋ Aggiungi categoria</button>}
          <div className="menu-settings-card">
            <label>Nome del menu<input value={menu.title} disabled={!canEdit} onChange={(event) => updateMenu((current) => ({ ...current, title: event.target.value }))} /></label>
            <label>Valuta<select value={menu.currency} disabled={!canEdit} onChange={(event) => updateMenu((current) => ({ ...current, currency: event.target.value as RestaurantMenu["currency"] }))}><option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option></select></label>
          </div>
        </aside>

        <section className="menu-items-panel">
          {selectedCategory ? <>
            <div className="category-editor-head">
              <div>
                <input className="category-name-input" value={selectedCategory.name} disabled={!canEdit} aria-label="Nome categoria" onChange={(event) => updateCategory(selectedCategory.id, { name: event.target.value })} />
                <input className="category-description-input" value={selectedCategory.description} disabled={!canEdit} aria-label="Descrizione categoria" placeholder="Aggiungi una descrizione facoltativa" onChange={(event) => updateCategory(selectedCategory.id, { description: event.target.value })} />
              </div>
              <div className="category-tools">
                <label><input type="checkbox" checked={selectedCategory.visible} disabled={!canEdit} onChange={(event) => updateCategory(selectedCategory.id, { visible: event.target.checked })} /> Visibile sul sito</label>
                <select value={selectedCategory.displayStyle} disabled={!canEdit} onChange={(event) => updateCategory(selectedCategory.id, { displayStyle: event.target.value as MenuCategory["displayStyle"] })}><option value="grid">Griglia con foto</option><option value="cards">Schede menu</option><option value="list">Lista compatta</option></select>
              </div>
            </div>
            <div className="menu-list-toolbar">
              <label>⌕<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca nel menu" /></label>
              {canEdit && <button className="primary" onClick={addItem}>＋ Nuovo elemento</button>}
            </div>
            <div className="menu-item-list">
              <div className="menu-item-head"><span>Elemento</span><span>Prezzo</span><span>Disponibilità</span><span /></div>
              {filtered.map((item) => {
                const actualIndex = selectedCategory.items.findIndex((current) => current.id === item.id);
                return <article key={item.id} className={selectedItemId === item.id ? "selected" : ""} onClick={() => setSelectedItemId(item.id)}>
                  <div className="menu-item-main">
                    <span className="menu-thumb">{item.imageUrl ? <img src={resolvedImage(item.imageUrl, siteBaseUrl)} alt="" /> : item.name.slice(0, 1)}</span>
                    <span><strong>{item.name}</strong><small>{item.description || "Nessuna descrizione"}</small></span>
                  </div>
                  <strong className="menu-price">{money(item.priceCents, menu.currency)}</strong>
                  <label className="availability" onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={item.available} disabled={!canEdit} onChange={(event) => updateItem(item.id, { available: event.target.checked })} /><span />{item.available ? "Disponibile" : "Esaurito"}</label>
                  <div className="menu-row-actions">
                    {canEdit && <><button aria-label="Sposta su" onClick={(event) => { event.stopPropagation(); updateCategory(selectedCategory.id, { items: move(selectedCategory.items, actualIndex, -1) }); }}>↑</button><button aria-label="Sposta giù" onClick={(event) => { event.stopPropagation(); updateCategory(selectedCategory.id, { items: move(selectedCategory.items, actualIndex, 1) }); }}>↓</button><button aria-label="Duplica" onClick={(event) => { event.stopPropagation(); duplicateItem(item); }}>⧉</button></>}
                    <button onClick={(event) => { event.stopPropagation(); setSelectedItemId(item.id); }}>Modifica</button>
                  </div>
                </article>;
              })}
              {!filtered.length && <div className="menu-empty"><strong>{search ? "Nessun risultato" : "Questa categoria è vuota"}</strong><p>{search ? "Prova con un altro nome." : "Aggiungi il primo piatto, drink o cocktail."}</p>{canEdit && !search && <button className="primary" onClick={addItem}>Aggiungi elemento</button>}</div>}
            </div>
            {canEdit && <button className="delete-category" onClick={() => { updateMenu((current) => ({ ...current, categories: current.categories.filter((category) => category.id !== selectedCategory.id) })); setSelectedCategoryId(menu.categories.find((category) => category.id !== selectedCategory.id)?.id || ""); setSelectedItemId(""); }}>Elimina categoria</button>}
          </> : <div className="menu-empty large"><strong>Crea la prima categoria</strong><p>Puoi usare categorie diverse per antipasti, piatti, vini, drink e cocktail.</p>{canEdit && <button className="primary" onClick={addCategory}>Aggiungi categoria</button>}</div>}
        </section>

        <aside className={`menu-item-editor ${selectedItem ? "open" : ""}`}>
          {selectedItem && selectedCategory ? <>
            <div className="item-editor-head"><div><small>Modifica elemento</small><h3>{selectedItem.name}</h3></div><button aria-label="Chiudi pannello" onClick={() => setSelectedItemId("")}>×</button></div>
            <div className="item-photo">
              {selectedItem.imageUrl ? <img src={resolvedImage(selectedItem.imageUrl, siteBaseUrl)} alt={selectedItem.name} /> : <span>Nessuna foto</span>}
              {canEdit && <label><input type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadImage(file); }} />{busy === "upload" ? "Caricamento…" : selectedItem.imageUrl ? "Cambia foto" : "Aggiungi foto"}</label>}
            </div>
            <div className="item-fields">
              <label>Nome<input value={selectedItem.name} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { name: event.target.value })} /></label>
              <label>Descrizione<textarea value={selectedItem.description} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { description: event.target.value })} /></label>
              <div className="field-pair"><label>Prezzo<input inputMode="decimal" value={selectedItem.priceCents === null ? "" : (selectedItem.priceCents / 100).toFixed(2).replace(".", ",")} disabled={!canEdit} placeholder="0,00" onChange={(event) => updateItem(selectedItem.id, { priceCents: toCents(event.target.value) })} /></label><label>Origine o nota<input value={selectedItem.origin} disabled={!canEdit} placeholder="Italia" onChange={(event) => updateItem(selectedItem.id, { origin: event.target.value })} /></label></div>
              <label>Etichette alimentari<input value={selectedItem.dietaryTags.join(", ")} disabled={!canEdit} placeholder="Vegetariano, senza glutine" onChange={(event) => updateItem(selectedItem.id, { dietaryTags: event.target.value.split(",").map((tag) => tag.trim()).filter(Boolean).slice(0, 20) })} /></label>
              <div className="item-checks"><label><input type="checkbox" checked={selectedItem.available} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { available: event.target.checked })} /> Disponibile</label><label><input type="checkbox" checked={selectedItem.featured} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { featured: event.target.checked })} /> In evidenza</label></div>
            </div>
            <section className="variant-editor">
              <div><strong>Varianti</strong>{canEdit && <button onClick={() => updateItem(selectedItem.id, { variants: [...selectedItem.variants, { id: crypto.randomUUID(), name: "Nuova variante", priceCents: selectedItem.priceCents, available: true }] })}>＋ Aggiungi</button>}</div>
              {selectedItem.variants.map((variant) => <article key={variant.id}><input aria-label="Nome variante" value={variant.name} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { variants: selectedItem.variants.map((current) => current.id === variant.id ? { ...current, name: event.target.value } : current) })} /><input aria-label="Prezzo variante" inputMode="decimal" value={variant.priceCents === null ? "" : (variant.priceCents / 100).toFixed(2).replace(".", ",")} disabled={!canEdit} onChange={(event) => updateItem(selectedItem.id, { variants: selectedItem.variants.map((current) => current.id === variant.id ? { ...current, priceCents: toCents(event.target.value) } : current) })} />{canEdit && <button aria-label="Elimina variante" onClick={() => updateItem(selectedItem.id, { variants: selectedItem.variants.filter((current) => current.id !== variant.id) })}>×</button>}</article>)}
              {!selectedItem.variants.length && <p>Aggiungi taglie, formati o opzioni con prezzi diversi.</p>}
            </section>
            {canEdit && <button className="delete-item" onClick={() => { updateCategory(selectedCategory.id, { items: selectedCategory.items.filter((item) => item.id !== selectedItem.id) }); setSelectedItemId(""); }}>Elimina elemento</button>}
          </> : <div className="item-editor-placeholder"><span>✎</span><strong>Seleziona un elemento</strong><p>Qui puoi modificare nome, descrizione, prezzo, foto, varianti e disponibilità.</p></div>}
        </aside>
      </div>
    </div>
  );
}
